export type RobotStatusAvailability = "disponible" | "indisponible";

export type RobotFleetCounterCategory =
  | "disponible"
  | "en_mission"
  | "en_panne"
  | "autre";

export type RobotClientCounterCategory =
  | "actif"
  | "en_reparation"
  | "hors_service"
  | "autre";

export interface RobotStatusDefinition<
  TValue extends string,
  TCounterCategory extends string,
> {
  value: TValue;
  label: string;
  availability: RobotStatusAvailability;
  counterCategory: TCounterCategory;
  known: boolean;
}

export const ROBOT_FLEET_STATUS_VALUES = {
  DISPONIBLE: "disponible",
  EN_MISSION: "en_mission",
  EN_PANNE: "en_panne",
  EN_REPARATION: "en_reparation",
  EN_PRET: "en_pret",
  EN_LOCATION: "en_location",
  HORS_SERVICE: "hors_service",
} as const;

export type RobotFleetStatus =
  typeof ROBOT_FLEET_STATUS_VALUES[keyof typeof ROBOT_FLEET_STATUS_VALUES];

export const ROBOT_FLEET_STATUSES: readonly RobotStatusDefinition<
  RobotFleetStatus,
  RobotFleetCounterCategory
>[] = [
  { value: "disponible", label: "Disponible", availability: "disponible", counterCategory: "disponible", known: true },
  { value: "en_mission", label: "En mission", availability: "indisponible", counterCategory: "en_mission", known: true },
  { value: "en_panne", label: "En panne", availability: "indisponible", counterCategory: "en_panne", known: true },
  { value: "en_reparation", label: "En réparation", availability: "indisponible", counterCategory: "en_panne", known: true },
  { value: "en_pret", label: "En prêt", availability: "indisponible", counterCategory: "autre", known: true },
  { value: "en_location", label: "En location", availability: "indisponible", counterCategory: "autre", known: true },
  { value: "hors_service", label: "Hors service", availability: "indisponible", counterCategory: "autre", known: true },
] as const;

export const ROBOT_FLEET_ASSIGNABLE_STATUSES = ROBOT_FLEET_STATUSES.filter(
  (status) => status.counterCategory !== "autre",
);

export const ROBOT_CLIENT_STATUS_VALUES = {
  ACTIF: "actif",
  EN_REPARATION: "en_reparation",
  HORS_SERVICE: "hors_service",
} as const;

export type RobotClientStatus =
  typeof ROBOT_CLIENT_STATUS_VALUES[keyof typeof ROBOT_CLIENT_STATUS_VALUES];

export const ROBOT_CLIENT_STATUSES: readonly RobotStatusDefinition<
  RobotClientStatus,
  RobotClientCounterCategory
>[] = [
  { value: "actif", label: "Actif", availability: "disponible", counterCategory: "actif", known: true },
  { value: "en_reparation", label: "En réparation", availability: "indisponible", counterCategory: "en_reparation", known: true },
  { value: "hors_service", label: "Hors service", availability: "indisponible", counterCategory: "hors_service", known: true },
] as const;

function unknownStatusLabel(value: string): string {
  const normalized = value.trim().replace(/_/g, " ");
  if (!normalized) return "Statut inconnu";
  return normalized;
}

function findStatusDefinition<
  TValue extends string,
  TCounterCategory extends string,
>(
  definitions: readonly RobotStatusDefinition<TValue, TCounterCategory>[],
  value: string | null | undefined,
  fallbackCategory: TCounterCategory,
): RobotStatusDefinition<string, TCounterCategory> {
  const known = definitions.find((definition) => definition.value === value);
  if (known) return known;
  return {
    value: value || "",
    label: unknownStatusLabel(value || ""),
    availability: "indisponible",
    counterCategory: fallbackCategory,
    known: false,
  };
}

export function getRobotFleetStatus(
  value: string | null | undefined,
): RobotStatusDefinition<string, RobotFleetCounterCategory> {
  return findStatusDefinition(ROBOT_FLEET_STATUSES, value, "autre");
}

export function getRobotClientStatus(
  value: string | null | undefined,
): RobotStatusDefinition<string, RobotClientCounterCategory> {
  return findStatusDefinition(ROBOT_CLIENT_STATUSES, value, "autre");
}

export function isRobotFleetAvailable(value: string | null | undefined): boolean {
  return getRobotFleetStatus(value).availability === "disponible";
}

export function isRobotClientAvailable(value: string | null | undefined): boolean {
  return getRobotClientStatus(value).availability === "disponible";
}